import { supabase } from './supabase'
import type { User, Session, AuthResponse } from '@supabase/supabase-js'

export interface AuthUser {
  id: string
  email: string | null
  name: string | null
  avatar_url: string | null
  user_role: 'user' | 'tester' | 'admin'
  default_privacy_mode: 'public' | 'friends'
  isochrone_profile: 'walking' | 'cycling' | 'driving'
  isochrone_minutes: number
}

export interface AuthState {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  error: string | null
}

/**
 * Sign up with email and password
 * Creates a new user account
 * Note: If email confirmation is enabled in Supabase, the user will be created
 * but won't be able to sign in until they confirm their email
 */
export async function signUpWithEmail(email: string, password: string): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // This will auto-confirm the email if auto-confirm is enabled in Supabase dashboard
        // Otherwise, user needs to confirm email before signing in
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      }
    })

    if (error) {
      console.error('Sign-up error:', error)
      throw error
    }

    // Check if user was auto-confirmed (session will exist if auto-confirm is enabled)
    if (!data.session) {
      console.warn('⚠️ Email confirmation required. User created but cannot sign in until email is confirmed.')
      console.log('💡 To disable email confirmation, go to Supabase Dashboard > Authentication > Providers > Email > Auto Confirm Users')
    }

    return { data, error: null }
  } catch (error) {
    console.error('Sign-up error:', error)
    throw error
  }
}

/**
 * Sign in with email and password
 * Authenticates an existing user
 */
export async function signInWithEmail(email: string, password: string): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      console.error('Sign-in error:', error)
      throw error
    }

    return { data, error: null }
  } catch (error) {
    console.error('Sign-in error:', error)
    throw error
  }
}

/**
 * Sign out the current user from all devices/sessions.
 *
 * Uses `scope: 'global'` (explicit, though it's the Supabase default) to revoke all
 * refresh tokens server-side, invalidating sessions across all devices. This prevents
 * race conditions where `autoRefreshToken` could complete a background token refresh
 * after signOut, re-authenticating the user.
 *
 * **Important behavior notes:**
 * - All refresh tokens are revoked immediately on the server
 * - Access tokens remain valid until their natural expiry (typically 1 hour)
 * - A very brief race window exists where an in-flight refresh could complete
 * - The `loggingOut` flag in userStore handles this edge case client-side
 * - Includes a 5-second timeout to prevent infinite hangs if Supabase is unresponsive
 *
 * This function handles the "Auth session missing" error gracefully - if there's no
 * active session to sign out from, it's not treated as an error since the end result
 * (user is logged out) is the same.
 *
 * @throws Error - Only throws for unexpected errors, NOT for missing session or timeout
 */
export async function signOut(): Promise<void> {
  const SIGNOUT_TIMEOUT_MS = 5000

  try {
    console.log('[signOut] Starting sign out process...')

    // Use scope: 'global' to invalidate all sessions server-side
    // This prevents race conditions where a background token refresh
    // could re-authenticate the user after signOut completes
    const signOutPromise = supabase.auth.signOut({ scope: 'global' })

    // Add timeout to prevent infinite hangs when Supabase client is in a bad state
    const timeoutPromise = new Promise<{ error: Error }>((resolve) =>
      setTimeout(() => resolve({ error: new Error('SignOut timeout after 5 seconds') }), SIGNOUT_TIMEOUT_MS)
    )

    const result = await Promise.race([signOutPromise, timeoutPromise])
    const error = result.error

    if (error) {
      // Handle timeout - treat as success since we'll clear local state anyway
      if (error.message?.includes('SignOut timeout')) {
        console.warn('⚠️ Sign-out timed out - clearing local state anyway')
        return // Don't throw - local state will be cleared by caller
      }
      // Handle "Auth session missing" gracefully - user is effectively already logged out
      if (error.name === 'AuthSessionMissingError' || error.message?.includes('session missing')) {
        console.warn('⚠️ Sign-out called but session already missing - treating as success')
        return // Don't throw - this is a valid end state
      }
      console.error('Sign-out error:', error)
      throw error
    }

    console.log('[signOut] Sign out successful')
  } catch (error: any) {
    // Handle timeout error thrown directly
    if (error?.message?.includes('SignOut timeout')) {
      console.warn('⚠️ Sign-out timed out - clearing local state anyway')
      return // Don't throw - local state will be cleared by caller
    }
    // Also handle the error if it's thrown directly
    if (error?.name === 'AuthSessionMissingError' || error?.message?.includes('session missing')) {
      console.warn('⚠️ Sign-out called but session already missing - treating as success')
      return // Don't throw - this is a valid end state
    }
    console.error('Sign-out error:', error)
    throw error
  }
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) {
      console.error('Get current user error:', error)
      return null
    }
    return user
  } catch (error) {
    console.error('Get current user error:', error)
    return null
  }
}

/**
 * Get the current session
 */
export async function getCurrentSession(): Promise<Session | null> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Get current session error:', error)
      return null
    }
    return session
  } catch (error) {
    console.error('Get current session error:', error)
    return null
  }
}

/**
 * Fetch user profile from the profiles table.
 *
 * Profile creation is handled automatically by the database trigger (handle_new_user)
 * when a new user is created in auth.users. This function fetches the existing profile
 * with retry logic to handle race conditions during email confirmation.
 *
 * @param user - The authenticated Supabase user object
 * @param retryCount - Internal parameter for retry logic (default: 0)
 * @returns Promise resolving to AuthUser object or null if fetch fails
 * @throws Never throws - always returns null on failure after retries
 *
 * @example
 * ```typescript
 * const authUser = await createOrUpdateProfile(supabaseUser)
 * if (authUser) {
 *   console.log(`Welcome ${authUser.name}!`)
 * }
 * ```
 */
export async function createOrUpdateProfile(user: User, retryCount: number = 0): Promise<AuthUser | null> {
  const MAX_RETRIES = 3
  const RETRY_DELAY_MS = 1000

  console.log(`🔍 [createOrUpdateProfile] Starting profile fetch for user ${user.id} (attempt ${retryCount + 1})`)

  try {
    // The database trigger (handle_new_user) automatically creates profiles
    // when new users are created in auth.users, so we just need to fetch it.
    const { data: profileData, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .single()

    if (fetchError) {
      // Handle different error cases
      if (fetchError.code === 'PGRST116') {
        // Profile not found - this can happen during email confirmation race condition
        // where the trigger hasn't run yet. Retry with exponential backoff.
        if (retryCount < MAX_RETRIES) {
          console.warn(`⏳ Profile not found (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS * (retryCount + 1)}ms...`)
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1)))
          return createOrUpdateProfile(user, retryCount + 1)
        }
        console.error('❌ Profile not found after maximum retries')
        return null
      }

      // Other errors (like RLS 403) - log and return null
      console.error('❌ Error fetching profile:', fetchError.code, fetchError.message, fetchError)
      return null
    }

    // Successfully fetched profile
    console.log(`✅ [createOrUpdateProfile] Profile fetched successfully:`, profileData.email)
    return {
      id: profileData.user_id,
      email: profileData.email,
      name: profileData.name,
      avatar_url: profileData.avatar_url,
      user_role: (profileData.user_role ?? 'user') as 'user' | 'tester' | 'admin',
      default_privacy_mode: (profileData.default_privacy_mode ?? 'friends') as 'public' | 'friends',
      isochrone_profile: (profileData.isochrone_profile ?? 'driving') as 'walking' | 'cycling' | 'driving',
      isochrone_minutes: profileData.isochrone_minutes ?? 15
    }
  } catch (error) {
    console.error('❌ Unexpected error fetching profile:', error)
    return null
  }
}

/**
 * Set up authentication state change listener
 */
export function onAuthStateChange(callback: (event: string, session: Session | null) => void) {
  return supabase.auth.onAuthStateChange(callback)
}

/**
 * Decode JWT token and extract user information
 * Note: This is mainly for debugging purposes as Supabase handles JWT automatically
 */
export function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('JWT decode error:', error)
    return null
  }
}

/**
 * Utility function to check if user has specific role
 */
export function hasRole(user: AuthUser | null, role: 'user' | 'tester' | 'admin'): boolean {
  if (!user) return false
  
  // Admin has access to everything
  if (user.user_role === 'admin') return true
  
  // Tester has access to user and tester features
  if (user.user_role === 'tester' && (role === 'user' || role === 'tester')) return true
  
  // Regular user only has access to user features
  return user.user_role === role
} 